import { useCallback } from "react"
import { graphql, useMutation, UseMutationConfig } from "react-relay"
import {
  Ch_generateReferralLetterDocumentInput,
  useChGenerateReferralDocumentMutation as useChGenerateReferralDocumentMutationType,
} from "@/modules/patients/detail/referral/ch/services/__generated__/useChGenerateReferralDocumentMutation.graphql"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"

const generateDocumentMutation = graphql`
  mutation useChGenerateReferralDocumentMutation($input: Ch_generateReferralLetterDocumentInput!) {
    ch_generateReferralLetterDocument(input: $input) {
      ch_ReferralLetter {
        fileId
        fileMetadata {
          documentType
          extension
          lengthInKilobytes
        }
        progressNoteId
        progressNote {
          pointOfCare {
            careProvider {
              firstName
              lastName
              title
              businessName
              creditorCodeRegister
              city
              postalCode
              streetAddress
            }
          }
        }
        createdAt
        createdBy
        patientId
        reasoning
        referralId
        signedAt
        updatedAt
        updatedBy
        medicationIds
        conditions
        allergies
        documentLanguage
        concludedAt
        concludedBy
        icdDiagnosis {
          id
          isAssociation
          diagnosisCode
          primaryCode
          secondaryCode
          associationType
          text
        }
      }
      errors {
        ... on DocumentDoesNotExist {
          message
        }
      }
    }
  }
`

type Config = Omit<UseMutationConfig<useChGenerateReferralDocumentMutationType>, "variables"> & {
  params: Omit<Ch_generateReferralLetterDocumentInput, "patientId">
}

const useChGenerateReferralDocumentMutation = () => {
  const [commit, isFlight] = useMutation<useChGenerateReferralDocumentMutationType>(generateDocumentMutation)
  const patientById = usePatientParam()

  const commitHandler = useCallback(
    (config: Config) => {
      commit({
        variables: {
          input: {
            patientId: patientById,
            allergies: config.params.allergies,
            conditions: config.params.conditions,
            dateOfConsultation: config.params.dateOfConsultation,
            episodeOfCare: config.params.episodeOfCare,
            id: config.params.id,
            keepOriginalAuthor: config.params.keepOriginalAuthor,
            medications: config.params.medications,
            reasoning: config.params.reasoning,
          },
        },
        ...config,
      })
    },
    [commit, patientById],
  )

  return [commitHandler, isFlight] as const
}

export { useChGenerateReferralDocumentMutation }
